import deepmerge from 'deepmerge';
import moment from 'moment';
import { Reducer } from 'redux';
import formats from '../../../intl/formats';
import intl from '../../../intl/intl';
import messages from '../../../intl/messages';
import {
  AirFareOption,
  AirLeg,
  AirSegmentDetails,
  AirSegmentResponse,
  CarSegmentDetails,
  CarSegmentResponse,
  CarType,
  HotelRateOption,
  HotelSegmentDetails,
  HotelSegmentResponse,
  OptionDetails,
  SegmentDetails,
  SegmentResponse,
} from '../../../models/itinerary';
import { formatFlightNumber } from '../../../utils/format';
import { unique } from '../../../utils/helpers';
import { ActionTypes } from '../../constants/action-types';
import { ItineraryReceiveAction } from '../itinerary/types';
import {
  bulletSeparated,
  commaSeparated,
  pluck,
} from './../../../utils/helpers';
import {
  SegmentDetailsReducerActions,
  SegmentDetailsUpdateAction,
} from './types';

const { formatMessage, formatNumber } = intl;

export interface SegmentDetailsReducerState {
  [segmentId: string]: SegmentDetails;
}

export const segmentDetailsInitialState: SegmentDetailsReducerState = {};

const timeFormat = 'h:mm a';
const dateTimeFormat = 'h:mm a ddd D MMM';

export const getDiffDays = (startDate: Date, endDate: Date): number => {
  return moment(endDate)
    .startOf('day')
    .diff(moment(startDate).startOf('day'), 'day');
};

export const getOvernightLabelText = (diffDays: number): string | undefined => {
  switch (diffDays) {
    case 1:
      return formatMessage(messages.flight.arrivalInfo.nextDay.label);
    case 2:
      return formatMessage(messages.flight.arrivalInfo.twoDaysLater.label);
    case -1:
      return formatMessage(messages.flight.arrivalInfo.previousDay.label);
    default:
      return undefined;
  }
};

export const getAircraftDetails = (legs: AirLeg[]): string => {
  const aircraftDetails = legs.map(leg => {
    const { airlineName, flightNumber } = leg;
    const formattedFlightNumber = formatFlightNumber(flightNumber);
    const operatedByAirlineName = !!leg.operatedByAirlineName;
    return formatMessage(messages.flight.leg, {
      airlineName,
      operatedByAirlineName,
      flightNumber: formattedFlightNumber,
    });
  });

  const filteredAircraftDetails = unique(aircraftDetails)
    // This is use to combine providers together
    .reduce((previous: string[], current, index) => {
      if (index === 0) return [current];
      const flightRegex = / [^ ]+$/;
      const previousFlightDetails: string = aircraftDetails[index - 1];
      const previousProvider = previousFlightDetails.replace(flightRegex, '');
      const currentProvider = current.replace(flightRegex, '');
      if (previousProvider === currentProvider) {
        return [...previous, current.replace(currentProvider, '').trim()];
      } else {
        return [...previous, current];
      }
    }, [])
    .join(', ');

  return filteredAircraftDetails;
};

/**
 * Group flight numbers by operator
 */
const groupLegsByOperatedAirline = (
  legs: AirLeg[],
): Record<AirLeg['operatedByAirlineName'], AirLeg[]> => {
  return legs.reduce(
    (
      previous: Partial<Record<AirLeg['operatedByAirlineName'], AirLeg[]>>,
      leg: AirLeg,
    ) => {
      const { operatedByAirlineName } = leg;

      if (!!operatedByAirlineName) {
        return deepmerge(previous, {
          [operatedByAirlineName]: [leg],
        });
      } else {
        return previous;
      }
    },
    {},
  ) as Record<AirLeg['operatedByAirlineName'], AirLeg[]>;
};

/**
 * @example
 * "*NZ1850 operated by Qantas Airways"
 */
export const getAircraftCodeshareDetails = (legs: AirLeg[]): string => {
  const groupedByOperator = groupLegsByOperatedAirline(legs);

  // Map the values, then filter out the empty strings
  const aircraftOperatedBy = Object.entries(groupedByOperator)
    .map(([airlineName, operatedLegs], index) => {
      if (!airlineName) return '';
      const flightNumbers = commaSeparated(
        pluck('flightNumber', operatedLegs).map(formatFlightNumber),
      );

      const showAsterisk = index === 0;

      return formatMessage(messages.flight.operatedBy, {
        showAsterisk,
        flightNumbers,
        airlineName,
      });
    })
    .filter(Boolean);

  return commaSeparated(aircraftOperatedBy);
};

/**
 * Format text (`+1`/`+2`/`-2`) how many days later the second leg is (e.g. an overnight flight)
 *
 * @example
 * calculateOvernight(new Date("2019-02-01", new Date("2019-02-02")))
 * // returns "+1"
 *
 * @example
 * calculateOvernight(new Date("2019-02-01", new Date("2019-01-31")))
 * // returns "-1"
 *
 * @example
 * calculateOvernight(new Date("2019-02-01", new Date("2019-02-03")))
 * // returns "+2"
 */
const calculateOvernight = (from: Date, to: Date): string | undefined => {
  return getOvernightLabelText(getDiffDays(from, to));
};

/**
 *
 * @example
 * //{
 * //  overnight: "+1",
 * //  codeshareDetails: "*NZ1850 operated by Qantas Airways"
 * //}
 */
const getAirOptionDetails = ({
  arrivalDate,
  departureDate,
  legs,
}: AirSegmentDetails): OptionDetails => {
  return {
    overnight: calculateOvernight(departureDate, arrivalDate),
    codeshareDetails: getAircraftCodeshareDetails(legs),
  };
};

export const getAirSegmentDetails = (
  segment: AirSegmentResponse,
  { arrivalDate, departureDate, legs }: AirSegmentDetails,
  fare: AirFareOption,
): SegmentDetails => {
  const overnight = calculateOvernight(departureDate, arrivalDate);

  const travelTimes = formatMessage(messages.flight.travelTimes, {
    departureTime: moment(departureDate).format(timeFormat),
    arrivalTime: moment(arrivalDate).format(timeFormat),
    overnight: !!overnight,
    overnightLabel: overnight,
  });

  const aircraftDetails = getAircraftDetails(legs);

  const fareBand = fare.fareFamily;

  const { departureCityName, arrivalCityName } = segment;

  const flights = [segment.recommended, ...(segment.shortlists || [])];

  const optionDetails = flights.reduce((previous, current) => {
    return { ...previous, [current.flightId]: getAirOptionDetails(current) };
  }, {});

  const segmentDetails: SegmentDetails = {
    details: bulletSeparated([travelTimes, aircraftDetails, fareBand]),
    // Displaying as USD as AUD causes react-intl to render as A$123.45
    // This is a chronium bug/feature depending how you look at it
    price: formatNumber(fare.totalCost, formats.number.USD),
    title: formatMessage(messages.flight.title, {
      departureCityName,
      arrivalCityName,
    }),
    options: 1 + (segment.shortlists && segment.shortlists.length),
    optionDetails,
    type: 'Air',
  };

  return segmentDetails;
};

const getHotelSegmentDetails = (
  segment: HotelSegmentResponse,
  hotel: HotelSegmentDetails,
  rate: HotelRateOption,
  travellerCount: number,
): SegmentDetails => {
  const checkInTime = moment(segment.checkIn).startOf('day');
  const checkOutTime = moment(segment.checkOut).startOf('day');

  const numberOfNights = checkOutTime.diff(checkInTime, 'days');

  const roomsAndNights = formatMessage(messages.hotel.roomAndNights, {
    rooms: formatMessage(messages.hotel.rooms, {
      numberOfRooms: travellerCount,
    }),
    nights: formatMessage(messages.hotel.nights, {
      numberOfNights,
    }),
  });

  const checkInTimeFormatted = checkInTime.format(dateTimeFormat);

  const segmentDetails: SegmentDetails = {
    details: bulletSeparated([
      hotel.name,
      checkInTimeFormatted,
      roomsAndNights,
    ]),
    // See comment above about USD
    price: formatNumber(rate.cost * numberOfNights, formats.number.USD),
    title: formatMessage(messages.hotel.title, { cityName: segment.cityName }),
    options: 1 + (segment.shortlists && segment.shortlists.length),
    optionDetails: {},
    nights: numberOfNights,
    type: 'Hotel',
  };

  return segmentDetails;
};

const getCarSegmentDetails = (
  segment: CarSegmentResponse,
  car: CarSegmentDetails,
  type: CarType,
) => {
  const providerName = car.supplierName;

  const pickupDateTime = moment(segment.pickupDate).startOf('day');
  const dropoffDateTime = moment(segment.dropoffDate).startOf('day');

  const days = dropoffDateTime.diff(pickupDateTime, 'days');

  const daysFormatted = formatMessage(messages.car.days, { days });

  const pickupDateTimeFormatted = pickupDateTime.format(dateTimeFormat);

  const segmentDetails: SegmentDetails = {
    details: bulletSeparated([
      providerName,
      pickupDateTimeFormatted,
      daysFormatted,
    ]),
    // See comment above about USD
    price: formatNumber(type.totalAmount, formats.number.USD),
    title: formatMessage(messages.car.title, {
      cityName: segment.pickupCityName,
    }),
    options: 1 + (segment.shortlists && segment.shortlists.length),
    optionDetails: {},
    days,
    type: 'Car',
  };

  return segmentDetails;
};

const getSegmentDetails = (
  segment: SegmentResponse,
  travellerCount: number,
) => {
  switch (segment.segmentType) {
    case 'Air':
      const airSegment = segment.airSegment!;
      const recommendedFlight = airSegment.recommended;
      const recommendedFare = recommendedFlight.fareOptions.find(
        fare => fare.recommended,
      )!;
      return getAirSegmentDetails(
        airSegment,
        recommendedFlight,
        recommendedFare,
      );
      break;
    case 'Hotel':
      const hotelSegment = segment.hotelSegment!;
      const recommendedHotel = hotelSegment.recommended;
      const recommendedRate = recommendedHotel.rates.find(
        hotelRate => hotelRate.recommended,
      )!;

      return getHotelSegmentDetails(
        hotelSegment,
        recommendedHotel,
        recommendedRate,
        travellerCount,
      );
      break;
    case 'Car':
      const carSegment = segment.carSegment!;
      const recommenedCar = carSegment.recommended;
      const recommendedType = recommenedCar.carTypes.find(
        type => type.recommended,
      )!;

      return getCarSegmentDetails(carSegment, recommenedCar, recommendedType);
  }
};

export const SegmentDetailsReducer: Reducer<
  SegmentDetailsReducerState,
  SegmentDetailsReducerActions
> = (
  state = segmentDetailsInitialState,
  action: SegmentDetailsReducerActions,
): SegmentDetailsReducerState => {
  switch (action.type) {
    // Map the incoming itinerary data to setup default options
    case ActionTypes.ITINERARY_GET_RECEIVE:
      const itinerary = (action as ItineraryReceiveAction).data.itinerary;

      return itinerary.reduce((previousValue, itinerarySegment) => {
        return {
          ...previousValue,
          [itinerarySegment.orderId]: getSegmentDetails(
            itinerarySegment,
            (action as ItineraryReceiveAction).data.travellers.length,
          )!,
        };
      }, {});
    case ActionTypes.SEGMENT_DETAILS_UPDATE:
      const {
        segment,
        option,
        rate,
        travellerCount,
      } = action as SegmentDetailsUpdateAction;
      let segmentDetails: SegmentDetails;
      switch (segment.segmentType) {
        case 'Air':
          segmentDetails = getAirSegmentDetails(
            (segment as SegmentResponse).airSegment!,
            option as AirSegmentDetails,
            rate as AirFareOption,
          );
          break;
        case 'Hotel':
          segmentDetails = getHotelSegmentDetails(
            (segment as SegmentResponse).hotelSegment!,
            option as HotelSegmentDetails,
            rate as HotelRateOption,
            travellerCount,
          );
          break;
        case 'Car':
          segmentDetails = getCarSegmentDetails(
            (segment as SegmentResponse).carSegment!,
            option as CarSegmentDetails,
            rate as CarType,
          );
          break;
      }

      return {
        ...state,
        [segment.orderId]: segmentDetails!,
      };
    default:
      return state;
  }
};
